/**
 * The rows a store holds after the answer of a read arrives, for a store that realtime also writes
 * (app#437).
 *
 * A read and the realtime channel are two writers of one array, and the answer of a read is as
 * old as the moment the database made it. So:
 *
 * - A row the read COVERS takes the answer: it is replaced, added, or removed when the answer
 *   does not have it. That is what repairs an event the channel lost.
 * - ⚠⚠ EXCEPT a row that an event changed AFTER the read was sent (`newer`). The event is the
 *   later fact, so the store stands for that row, held or not held. Without this, a service
 *   published during a read is removed again by the answer, and a service deleted during a read
 *   comes back.
 * - A row the read does NOT cover stays. A read of one node must not remove the rows of the
 *   others, and a full read that was sent before a node entered the store must not remove the
 *   rows that the node's own read brought.
 * - No id is held twice: a held row whose id the answer has gives way to the answer, covered or not.
 */
export function rowsAfterRead<T extends { id: string }>(
  held: T[],
  read: T[],
  covers: (row: T) => boolean,
  newer: (id: string) => boolean
): T[] {
  const answer = read.filter(row => !newer(row.id))
  const answered = new Set(answer.map(row => row.id))
  return [...answer, ...held.filter(row => !answered.has(row.id) && (!covers(row) || newer(row.id)))]
}

/**
 * The numbers behind `newer`: which rows an event changed after a read was SENT.
 *
 * A store that realtime also writes keeps ONE clock. The realtime handler calls `mark(id)` for
 * each event it applies. A read calls `readStarts()` at the moment it is sent, and gives the
 * returned test to `rowsAfterRead` when its answer arrives.
 *
 * ⚠ `readStarts()` is called BEFORE the request, never when the answer arrives. A test that is
 * made at the answer reads every event as old, and the answer then overwrites each of them.
 */
export function eventClock() {
  let last = 0
  const at = new Map<string, number>()
  return {
    /** The realtime handler applied an event for this id. */
    mark: (id: string) => void at.set(id, ++last),
    /** A read is sent now. The result says if an event changed that id after this moment. */
    readStarts: () => {
      const since = last
      return (id: string) => (at.get(id) ?? 0) > since
    },
  }
}
